/**
 * Briefly flashes a purple highlight on an element to draw the user's eye to
 * the section they just navigated to.
 *
 * The flash is triggered via IntersectionObserver so it starts exactly when
 * the element scrolls into view — otherwise a smooth scroll would still be in
 * progress and the highlight could finish before the user sees it.
 */
export function flashTarget(el: HTMLElement): void {
  let flashed = false;

  const run = () => {
    if (flashed) return;
    flashed = true;
    el.classList.remove('section-flash');
    // Force a reflow so re-adding the class restarts the animation from 0%.
    void el.offsetWidth;
    el.classList.add('section-flash');
    el.addEventListener(
      'animationend',
      () => el.classList.remove('section-flash'),
      { once: true },
    );
  };

  if (typeof IntersectionObserver === 'undefined') {
    run();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          run();
          observer.disconnect();
        }
      });
    },
    { threshold: 0.1 },
  );

  observer.observe(el);

  // Fallback: if the observer never fires for any reason, flash anyway after a
  // short delay so the highlight is never silently dropped.
  window.setTimeout(() => {
    observer.disconnect();
    run();
  }, 1500);
}

export default flashTarget;